import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Select,
  SimpleGrid,
  Text,
  useColorModeValue,
  Icon,
  Badge,
  Tooltip,
  Heading,
  HStack,
  Divider,
  VStack,
} from "@chakra-ui/react";
import ReactSelect from "react-select";

import {
  CalendarIcon,
  ChevronRightIcon,
  SettingsIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  FiActivity,
  FiClock,
  FiZap,
  FiBarChart2,
  FiPieChart,
  FiTrendingUp,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { toastState } from "atoms/toastState";
import TotalRevenue from "./TotalRevenue";
import TotalDuration from "./TotalDuration";
import TotalSessions from "./TotalSessions";
import TotalUnits from "./TotalUnits";
import AvgSessionsTime from "./AvgSessionsTime";
import AvgUnitsPerSession from "./AvgUnitsPerSession";
import AvgSessionsPerDay from "./AvgSessionsPerDay";
import HeatMap from "./HeatMap";
import logger from "infra/logger";
import { getFirstDateOfMonth } from "utils/utils";
import { getCurrentDate } from "utils/utils";
import { dynamicStatsSelectOptions } from "../variables/dynamicStatsSelectOptions";

// Styled components for glass morphism effect
const MotionBox = motion(Box);

const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.85)",
    "rgba(26, 32, 44, 0.85)"
  );
  const borderColor = useColorModeValue(
    "rgba(35, 169, 121, 0.3)", // Theme color with opacity
    "rgba(35, 169, 121, 0.2)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(12px)"
      borderRadius="2xl"
      boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.25)"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "0 8px 32px 0 rgba(35, 169, 121, 0.15)" }}
      position="relative"
      {...rest}
    >
      {children}
    </Box>
  );
};

// Component to display formatted time with styled units
const TimeDisplay = ({ timeString }) => {
  if (!timeString) return "--";

  // Function to parse time parts and create styled elements
  const renderTimeParts = () => {
    const parts = [];

    // Define darker color variants
    const colors = {
      days: {
        number: "#222",
        unit: "brand.500", // brand green
      },
      hours: {
        number: "#222",
        unit: "#2a69ac", // Darker blue
      },
      minutes: {
        number: "#222",
        unit: "#2f855a", // Darker green
      },
      seconds: {
        number: "#222",
        unit: "#c05621", // Darker amber
      },
    };

    // Parse days
    const daysMatch = timeString.match(/(\d+)d/);
    if (daysMatch) {
      parts.push(
        <Box
          key="days"
          as="span"
          display="inline-flex"
          alignItems="baseline"
          mr={1}
        >
          <Text
            as="span"
            fontWeight="bold"
            fontSize="2xl"
            color={colors.days.number}
          >
            {daysMatch[1]}
          </Text>
          <Text
            as="span"
            fontSize="xs"
            color={colors.days.unit}
            fontWeight="semibold"
            ml={0.5}
          >
            d
          </Text>
        </Box>
      );
    }

    // Parse hours
    const hoursMatch = timeString.match(/(\d+)h/);
    if (hoursMatch) {
      parts.push(
        <Box
          key="hours"
          as="span"
          display="inline-flex"
          alignItems="baseline"
          mr={1}
        >
          <Text
            as="span"
            fontWeight="bold"
            fontSize="2xl"
            color={colors.hours.number}
          >
            {hoursMatch[1]}
          </Text>
          <Text
            as="span"
            fontSize="xs"
            color={colors.hours.unit}
            fontWeight="semibold"
            ml={0.5}
          >
            h
          </Text>
        </Box>
      );
    }

    // Parse minutes
    const minutesMatch = timeString.match(/(\d+)m/);
    if (minutesMatch) {
      parts.push(
        <Box
          key="minutes"
          as="span"
          display="inline-flex"
          alignItems="baseline"
          mr={1}
        >
          <Text
            as="span"
            fontWeight="bold"
            fontSize="2xl"
            color={colors.minutes.number}
          >
            {minutesMatch[1]}
          </Text>
          <Text
            as="span"
            fontSize="xs"
            color={colors.minutes.unit}
            fontWeight="semibold"
            ml={0.5}
          >
            m
          </Text>
        </Box>
      );
    }

    // Parse seconds
    const secondsMatch = timeString.match(/(\d+)s/);
    if (secondsMatch) {
      parts.push(
        <Box
          key="seconds"
          as="span"
          display="inline-flex"
          alignItems="baseline"
        >
          <Text
            as="span"
            fontWeight="bold"
            fontSize="2xl"
            color={colors.seconds.number}
          >
            {secondsMatch[1]}
          </Text>
          <Text
            as="span"
            fontSize="xs"
            color={colors.seconds.unit}
            fontWeight="semibold"
            ml={0.5}
          >
            s
          </Text>
        </Box>
      );
    }

    return parts.length > 0 ? parts : timeString;
  };

  return (
    <Flex alignItems="center" flexWrap="wrap">
      {renderTimeParts()}
    </Flex>
  );
};

const StatsCard = ({
  icon,
  title,
  value,
  subtitle,
  color,
  isTimeValue,
  ...rest
}) => {
  const borderColor = useColorModeValue(
    "rgba(35, 169, 121, 0.4)",
    "whiteAlpha.100"
  );

  return (
    <Box
      bg="white"
      borderRadius="xl"
      boxShadow="0 1px 4px rgba(0,0,0,0.06)"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ transform: "translateY(-5px)", boxShadow: "xl" }}
      height="100%"
      minH="145px"
      flex="0 0 auto"
      width={{
        base: "100%",
        md: "calc(50% - 10px)",
        lg: "calc(33.333% - 14px)",
        xl: "calc(25% - 15px)",
      }}
      maxW="280px"
      position="relative"
      p={5}
      {...rest}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontSize="md" fontWeight="semibold" color="gray.700">
          {title}
        </Text>
        <Icon as={icon} w={5} h={5} color={color || "brand.500"} />
      </Flex>
      {isTimeValue ? (
        <Box mb={2}>
          <TimeDisplay timeString={value} />
        </Box>
      ) : (
        <Text fontSize="2xl" fontWeight="bold" mb={2}>
          {value}
        </Text>
      )}
      {subtitle && (
        <Text fontSize="xs" color="gray.600">
          {subtitle}
        </Text>
      )}
    </Box>
  );
};

const CustomIconButton = ({ icon, label, onClick, ...rest }) => {
  return (
    <Button
      leftIcon={<Icon as={icon} />}
      onClick={onClick}
      bgColor="brand.500"
      color="white"
      _hover={{
        bgColor: "#e86d26",
        transform: "translateY(-2px)",
        boxShadow: "md",
      }}
      _active={{ bgColor: "#d55d16" }}
      borderRadius="xl"
      size="md"
      {...rest}
    >
      {label}
    </Button>
  );
};

// Format duration in the format: 01d:19h:02m:55s or 33m:06s
const formatDuration = (durationInput) => {
  // Handle null, undefined or other invalid values
  if (durationInput === null || durationInput === undefined) return "--";

  // First, try to handle if it's a numeric value in seconds
  if (typeof durationInput === "number" || !isNaN(Number(durationInput))) {
    const totalSeconds = Number(durationInput);

    // Calculate days, hours, minutes, seconds
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    // Format for visual display with unit indicators
    if (days > 0) {
      return `${days.toString().padStart(2, "0")}d ${hours
        .toString()
        .padStart(2, "0")}h ${minutes.toString().padStart(2, "0")}m ${seconds
        .toString()
        .padStart(2, "0")}s`;
    } else if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}h ${minutes
        .toString()
        .padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
    } else {
      return `${minutes.toString().padStart(2, "0")}m ${seconds
        .toString()
        .padStart(2, "0")}s`;
    }
  }

  // Convert to string for string operations
  const durationStr = String(durationInput);

  // If it's already in the proper format, return it
  if (
    durationStr.includes("d:") ||
    durationStr.includes("h:") ||
    durationStr.includes("m:") ||
    durationStr.includes("s")
  ) {
    return durationStr;
  }

  // Parse HH:MM:SS format
  const parts = durationStr.split(":");
  if (parts.length === 3) {
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);

    // Handle invalid numbers
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      return "--";
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    // Format with days if more than 24 hours
    if (days > 0) {
      return `${days.toString().padStart(2, "0")}d:${remainingHours
        .toString()
        .padStart(2, "0")}h:${minutes.toString().padStart(2, "0")}m:${seconds
        .toString()
        .padStart(2, "0")}s`;
    }
    // Format with hours if more than 60 minutes
    else if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}h:${minutes
        .toString()
        .padStart(2, "0")}m:${seconds.toString().padStart(2, "0")}s`;
    }
    // Format with just minutes and seconds
    else {
      return `${minutes.toString().padStart(2, "0")}m:${seconds
        .toString()
        .padStart(2, "0")}s`;
    }
  }

  return durationStr; // Return converted string if we can't parse
};

const DynamicStats = () => {
  const themeColor = "brand.500";
  const setToast = useSetRecoilState(toastState);
  const [chartData, setChartData] = useState({});
  const [heatMapdata, setHeatMapData] = useState([[]]);
  const [points, setPoints] = useState([]);
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);

  // Reference for portal container
  useEffect(() => {
    setMenuPortalTarget(document.body);
  }, []);
  const [{ data, loading, error }, getCustomStats, cancelCustomRequest] =
    useAxios(
      {
        method: "GET",
        url: `/api/dashboard/analytics?key=custom_stats`,
      },
      { manual: true }
    );

  const [
    { data: heatmapData, loading: heatmapLoading, error: heatmapError },
    getHeatMapData,
    cancelHeatmapRequest,
  ] = useAxios(
    {
      method: "GET",
      url: `/api/dashboard/analytics?key=heatmap_data`,
    },
    { manual: true }
  );

  const [
    {
      data: chargePointsData,
      loading: chargePointsLoading,
      error: chargePointsError,
    },
    getPoints,
    cancelPointsRequest,
  ] = useAxios(
    {
      method: "GET",
      url: `/api/stations`,
    },
    { manual: true }
  );

  const validationSchema = yup.object().shape({
    From: yup.date().required("From is required"),
    To: yup.date().required("To is required"),
    ChargePoint: yup.string().required("chargePoint is Mandatory"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  // Function to get the first date of the current month

  const getStats = async () => {
    const formValues = getValues();
    let params = {};
    if (formValues.From && formValues.To && formValues.ChargePoint != "") {
      params["startTime"] = formValues.From;
      params["endTime"] = formValues.To;
      params["chargePointCode"] = formValues.ChargePoint;
    } else {
      params["startTime"] = getFirstDateOfMonth();
      params["endTime"] = getCurrentDate();
      params["chargePointCode"] = "all";
    }
    try {
      const response = await getCustomStats({ params });
      setChartData(response.data.data);
      const heatmapResponse = await getHeatMapData({ params });
      setHeatMapData(heatmapResponse.data.data);
    } catch (error) {
      logger.error("Error fetching data:", error);
      setToast({
        isOpen: true,
        title: `Error occured while getting stats`,
        status: "error",
      });
    }
  };

  const getChargePoints = async () => {
    try {
      const response = await getPoints();
      setPoints(
        Array.isArray(response?.data?.data) ? response.data.data : []
      );
    } catch (error) {
      logger.log(error);
    }
  };

  useEffect(() => {
    const formValues = getValues();
    setValue("ChargePoint", "all");
    const timeout = setTimeout(() => {
      if (formValues.From && formValues.To) {
        getStats();
      }
    }, 500);
    return () => {
      clearTimeout(timeout);
      cancelCustomRequest();
      cancelHeatmapRequest();
    };
  }, []);
  useEffect(() => {
    const timeout = setTimeout(() => {
      getChargePoints();
    }, 1000);
    return () => {
      clearTimeout(timeout);
      cancelPointsRequest();
    };
  }, []);
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
      mb={5}
      // backgroundImage="linear-gradient(180deg, rgba(35, 169, 121, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
      pt={2}
      borderRadius="3xl"
    >
      <GlassMorphismCard mb={6}>
        <Box p={6}>
          <Flex justify="space-between" align="center" mb={6}>
            <HStack spacing={3}>
              <Icon as={FiActivity} color="brand.500" w={7} h={7} />
              <Heading size="lg" fontWeight="600" color="gray.700">
                Overview
              </Heading>
              <Badge
                colorScheme="brand"
                fontSize="xs"
                borderRadius="full"
                px={2}
              >
                Live Data
              </Badge>
            </HStack>
            <Tooltip label="Dashboard Settings" placement="top">
              <Icon
                as={SettingsIcon}
                color="gray.400"
                w={5}
                h={5}
                cursor="pointer"
                _hover={{ color: "brand.500", transform: "rotate(90deg)" }}
                transition="all 0.3s ease"
              />
            </Tooltip>
          </Flex>

          <Divider mb={6} borderColor="gray.200" />

          <Flex
            direction={{ base: "column", md: "row" }}
            alignItems={{ base: "stretch", md: "center" }}
            justifyContent="flex-start"
            gap={{ base: 3, md: 6 }}
            flexWrap="wrap"
            mb={4}
          >
            {dynamicStatsSelectOptions.map((item) => {
              return (
                <Controller
                  key={item.name}
                  name={item.name}
                  control={control}
                  defaultValue={item.defaultValue}
                  render={({ field }) => (
                    <FormControl
                      isRequired
                      flexDirection="column"
                      isInvalid={errors[item.name]}
                      width={{ base: "100%", md: "auto" }}
                      position="relative"
                    >
                      <HStack mb={1} spacing={1}>
                        <FormLabel
                          fontSize="sm"
                          fontWeight="semibold"
                          color="gray.700"
                          mb={0}
                        >
                          {item.label}
                        </FormLabel>
                        {item.helper && (
                          <Tooltip label={item.helper} placement="top">
                            <InfoIcon color="gray.400" boxSize={3} />
                          </Tooltip>
                        )}
                      </HStack>

                      {item.type === "datetime-local" && (
                        <InputGroup size="md">
                          <InputLeftElement
                            pointerEvents="none"
                            children={
                              <Icon
                                as={CalendarIcon}
                                color={themeColor}
                                opacity={0.8}
                              />
                            }
                          />
                          <Input
                            {...field}
                            borderRadius="md"
                            background="white"
                            border="1px solid"
                            borderColor="gray.200"
                            focusBorderColor={themeColor}
                            fontSize="sm"
                            width={{ base: "100%", md: "250px" }}
                            type={item.type}
                            isRequired
                            _focus={
                              {
                                // boxShadow: `0 0 0 1px ${themeColor}`,
                                // borderColor: themeColor,
                              }
                            }
                            _hover={{
                              borderColor: "gray.300",
                            }}
                            transition="all 0.2s"
                            pl={10}
                            py={5}
                            h="38px"
                          />
                        </InputGroup>
                      )}
                      {item.type === "select" && (
                        <Box
                          width={{ base: "100%", md: "230px" }}
                          position="relative"
                          zIndex={5} // Higher z-index for proper dropdown display
                        >
                          <FormControl position="relative">
                            <ReactSelect
                              value={
                                field.value === "all"
                                  ? { value: "all", label: "All Chargers" }
                                  : points.find(
                                      (p) => p.StationId === field.value
                                    )
                                  ? {
                                      value: field.value,
                                      label: `${
                                        points.find(
                                          (p) => p.StationId === field.value
                                        ).StationId
                                      } - ${
                                        points.find(
                                          (p) => p.StationId === field.value
                                        ).Name
                                      }`,
                                    }
                                  : { value: "all", label: "All Chargers" }
                              }
                              onChange={(option) =>
                                field.onChange(option.value)
                              }
                              options={[
                                { value: "all", label: "All Chargers" },
                                ...points.map((point) => ({
                                  value: point.StationId,
                                  label: `${point.StationId} - ${point.Name}`,
                                })),
                              ]}
                              placeholder="Select or search..."
                              isSearchable={true}
                              menuPortalTarget={menuPortalTarget}
                              menuPosition="fixed"
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  height: "38px",
                                  minHeight: "38px",
                                  borderRadius: "0.375rem",
                                  borderColor: state.isFocused
                                    ? themeColor
                                    : "#E2E8F0",
                                  boxShadow: state.isFocused
                                    ? `0 0 0 1px ${themeColor}`
                                    : "none",
                                  "&:hover": {
                                    borderColor: themeColor,
                                  },
                                  backgroundColor: "white",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                menu: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                  marginTop: "4px",
                                  overflow: "hidden",
                                  boxShadow:
                                    "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: state.isSelected
                                    ? "#4299E1"
                                    : state.isFocused
                                    ? "rgba(35, 169, 121, 0.1)"
                                    : "white",
                                  color: state.isSelected ? "white" : "#2D3748",
                                  padding: "10px 12px",
                                  cursor: "pointer",
                                  fontSize: "0.875rem",
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  padding: "0 12px",
                                  height: "38px",
                                  display: "flex",
                                  alignItems: "center",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }),
                                singleValue: (base) => ({
                                  ...base,
                                  color: "#2D3748",
                                  fontSize: "0.875rem",
                                  fontWeight: "medium",
                                  margin: 0,
                                  position: "absolute",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  maxWidth: "calc(100% - 8px)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }),
                                dropdownIndicator: (base) => ({
                                  ...base,
                                  padding: "0 8px",
                                  color: "#718096",
                                }),
                                indicatorSeparator: () => ({
                                  display: "none",
                                }),
                              }}
                            />
                          </FormControl>
                        </Box>
                      )}
                      {errors[item.name] && (
                        <FormErrorMessage fontSize="xs" mt={1}>
                          {errors[item.name].message}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  )}
                />
              );
            })}
            <Button
              ml={{ base: 0, md: "auto" }}
              colorScheme="brand"
              bg="brand.500"
              borderRadius="md"
              px={6}
              py={0}
              h="38px"
              fontSize="sm"
              fontWeight="bold"
              color="white"
              onClick={handleSubmit(getStats)}
              isLoading={loading}
              loadingText="Fetching Data"
              _hover={{
                transform: "translateY(-1px)",
                boxShadow: "sm",
                bg: "brand.600",
              }}
              _active={{
                transform: "translateY(0)",
                bg: "brand.600",
              }}
              transition="all 0.2s"
              alignSelf="flex-end"
              leftIcon={<Icon as={FiActivity} boxSize={4} />}
            >
              Get Analytics
            </Button>
          </Flex>
        </Box>
      </GlassMorphismCard>

      <Flex
        flexWrap="wrap"
        justifyContent="flex-start"
        gap="20px"
        mb="20px"
        w="100%"
      >
        <StatsCard
          icon={FaRupeeSign}
          title="Total Revenue"
          value={
            chartData?.total_revenue
              ? `₹${chartData.total_revenue.toFixed(2)}`
              : "₹0.00"
          }
          subtitle="Total earnings from all sessions"
          color="brand.500"
        />
        <StatsCard
          icon={FiBarChart2}
          title="Total Sessions"
          value={chartData?.total_sessions || "0"}
          subtitle="Number of charging sessions"
          color="#4299E1" // blue
        />
        <StatsCard
          icon={FiZap}
          title="Total Units"
          value={
            chartData?.total_units
              ? `${chartData.total_units.toFixed(2)} kWh`
              : "0 kWh"
          }
          subtitle="Energy consumed across sessions"
          color="#48BB78" // green
        />
        <StatsCard
          icon={FiClock}
          title="Total Duration"
          value={formatDuration(chartData?.total_duration) || "--"}
          subtitle="Time spent charging"
          color="#805AD5" // purple
          isTimeValue={true}
        />
        <StatsCard
          icon={FiBarChart2}
          title="Avg. Sessions Per Day"
          value={
            chartData?.average_sessions_per_day
              ? chartData.average_sessions_per_day.toFixed(2)
              : "0"
          }
          subtitle="Daily session frequency"
          color="brand.500" 
        />
        <StatsCard
          icon={FiZap}
          title="Avg. Units Per Session"
          value={
            chartData?.average_units_per_session
              ? `${chartData.average_units_per_session.toFixed(2)} kWh`
              : "0 kWh"
          }
          subtitle="Energy consumed per session"
          color="#3182CE" // blue
        />
        <StatsCard
          icon={FiTrendingUp}
          title="Avg. Session Time"
          value={
            formatDuration(chartData?.average_duration_per_session) || "--"
          }
          subtitle="Average duration per session"
          color="brand.500" 
          isTimeValue={true}
        />
      </Flex>
      <GlassMorphismCard mb={4} overflow="visible" display={{ base: "none", md: "block" }}>
        <Box p={6}>
          <Flex justify="space-between" align="center" mb={5}>
            <HStack spacing={2}>
              <Icon as={FiBarChart2} color="brand.500" w={5} h={5} />
              <Text fontSize="lg" fontWeight="600" color="gray.700">
                Usage Heatmap
              </Text>
              <Badge
                colorScheme="brand"
                variant="subtle"
                fontSize="xs"
                borderRadius="full"
                px={2}
              >
                Hourly Analysis
              </Badge>
            </HStack>
            <Tooltip label="View Detailed Analytics" placement="top">
              <Icon
                as={ChevronRightIcon}
                color="gray.400"
                w={5}
                h={5}
                cursor="pointer"
                _hover={{ color: "brand.500", transform: "translateX(2px)" }}
                transition="all 0.3s ease"
              />
            </Tooltip>
          </Flex>

          <Box
            position="relative"
            zIndex="1"
            // bg={useColorModeValue("white", "gray.800")}
            borderRadius="xl"
            p={4}
            // boxShadow="sm"
            // border="1px solid"
            // borderColor={useColorModeValue("gray.100", "gray.700")}
            // _hover={{ boxShadow: "md", transition: "all 0.3s ease" }}
            _before={{
              content: '""',
              position: "absolute",
              top: "-5px",
              left: "-5px",
              right: "-5px",
              bottom: "-5px",
              // background:
              //   "linear-gradient(45deg, rgba(245,125,54,0.05) 0%, rgba(255,255,255,0) 70%)",
              borderRadius: "xl",
              zIndex: "-1",
            }}
          >
            <HeatMap data={heatMapdata} />
          </Box>
        </Box>
      </GlassMorphismCard>
    </MotionBox>
  );
};

export default DynamicStats;
